/** Rangos de la analítica: `from` inclusivo y `to` exclusivo, en DÍAS LOCALES de la zona elegida (como la API). */

export type RangePreset = '7d' | '30d' | '90d' | 'month' | 'custom';
export const DEFAULT_TZ = 'America/Santiago';

export const TIME_ZONES: { id: string; label: string }[] = [
  { id: 'America/Santiago', label: 'Santiago (Chile)' },
  { id: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires' },
  { id: 'America/Lima', label: 'Lima' },
  { id: 'America/Bogota', label: 'Bogotá' },
  { id: 'America/Mexico_City', label: 'Ciudad de México' },
  { id: 'America/Sao_Paulo', label: 'São Paulo' },
  { id: 'Europe/Madrid', label: 'Madrid' },
  { id: 'UTC', label: 'UTC' },
];

const DAY_MS = 86_400_000;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

export const isDay = (s: string | undefined): s is string =>
  s !== undefined && DATE.test(s) && !Number.isNaN(Date.parse(s));

export function addDays(day: string, n: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);
}

/** `YYYY-MM-DD` de hoy en `tz`. */
export function todayIn(tz: string, now: Date = new Date()): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(
      now,
    );
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

export function presetRange(preset: Exclude<RangePreset, 'custom'>, tz: string, now: Date = new Date()) {
  const today = todayIn(tz, now);
  if (preset === 'month') {
    const first = `${today.slice(0, 8)}01`;
    return { from: first, to: addDays(today, 1) };
  }
  const days = preset === '7d' ? 7 : preset === '30d' ? 30 : 90;
  return { from: addDays(today, -(days - 1)), to: addDays(today, 1) };
}

/** Rango efectivo (explícito y válido, o los últimos 30 días). */
export function resolveRange(
  search: { from?: string | undefined; to?: string | undefined },
  tz: string,
  now: Date = new Date(),
): { from: string; to: string } {
  const def = presetRange('30d', tz, now);
  const from = isDay(search.from) ? search.from : def.from;
  const to = isDay(search.to) ? search.to : isDay(search.from) ? addDays(todayIn(tz, now), 1) : def.to;
  const ordered = to > from ? { from, to } : { from, to: addDays(from, 1) };
  // La API admite hasta 366 días: se recorta el inicio para no pedir un rango que rechazaría.
  const days = Math.round((Date.parse(`${ordered.to}T00:00:00Z`) - Date.parse(`${ordered.from}T00:00:00Z`)) / DAY_MS);
  return days > 366 ? { from: addDays(ordered.to, -366), to: ordered.to } : ordered;
}

export function detectPreset(
  search: { from?: string | undefined; to?: string | undefined },
  tz: string,
  now: Date = new Date(),
): RangePreset {
  if (!search.from && !search.to) return '30d';
  const r = resolveRange(search, tz, now);
  for (const p of ['7d', '30d', '90d', 'month'] as const) {
    const pr = presetRange(p, tz, now);
    if (pr.from === r.from && pr.to === r.to) return p;
  }
  return 'custom';
}

export const daysInRange = (from: string, to: string): number =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);
